import { Check, Minus, X } from 'lucide-react';
import { Skeleton } from '@/shared/ui/skeleton';
import { EUX, etapesRaccordement, libelleManque } from '@/features/bridge/bridge-etat';
import ErreurBloc from '@/features/bridge/components/ErreurBloc';

/**
 * « Peut-il piloter ? » — la question qu'on se pose en ouvrant la page, en premier.
 *
 * Le verdict était sous le formulaire du contrat : on éditait ce qu'on annonçait à un
 * agrégateur avant de savoir s'il pouvait seulement nous appeler. Il passe en tête.
 *
 * Chaque étape dit QUI doit agir. C'est la ligne qui évite de chercher dans nos clés
 * un certificat que Survoltage ne nous a pas encore envoyé. Une étape à faire chez
 * nous mène à l'onglet où elle se fait.
 */
const ICONE = { true: Check, false: X, null: Minus };
const TEINTE = {
  true: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  false: 'border-amber-200 bg-amber-50 text-amber-700',
  null: 'border-neutral-200 bg-neutral-50 text-neutral-400',
};

function Etape({ etape, rang, label, surOnglet }) {
  const Icone = ICONE[String(etape.ok)];
  const cliquable = etape.ok === false && etape.onglet;
  const Enveloppe = cliquable ? 'button' : 'div';
  return (
    <Enveloppe
      {...(cliquable ? { type: 'button', onClick: () => surOnglet(etape.onglet) } : {})}
      data-testid={`bridge-etape-${etape.cle}`}
      className={`flex flex-1 items-start gap-3 rounded-md p-3 text-left ${cliquable
        ? 'transition-colors hover:bg-neutral-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-400'
        : ''}`}>
      <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full border ${TEINTE[String(etape.ok)]}`}>
        <Icone className="h-3.5 w-3.5" aria-hidden />
      </span>
      <span className="min-w-0">
        <span className="block text-[12px] text-neutral-500">{rang}. {etape.titre}</span>
        <span className="block text-[13.5px] font-medium text-neutral-900">
          {etape.resume || '—'}
        </span>
        {etape.quiAgit && (
          <span className={`mt-1 inline-block rounded px-1.5 py-0.5 text-[11.5px] ${etape.quiAgit === EUX
            ? 'bg-neutral-100 text-neutral-700' : 'bg-amber-100 text-amber-800'}`}
                data-testid={`bridge-qui-${etape.cle}`}>
            {etape.quiAgit === EUX ? `à faire par ${label}` : 'à faire chez nous'}
            {cliquable && ' →'}
          </span>
        )}
        {etape.manques?.length > 0 && (
          <span className="mt-1 block font-mono text-[11px] text-neutral-400"
                title={etape.manques.map(libelleManque).join(', ')}>
            {etape.manques.join(' · ')}
          </span>
        )}
      </span>
    </Enveloppe>
  );
}

const COMPTEURS = [
  ['actifs_declares', 'Déclarés'],
  ['actifs_joignables', 'Joignables'],
  ['actifs_sous_mandat', 'Sous mandat'],
  ['ordres_7_jours', 'Ordres sur 7 jours'],
];

export default function VerdictRaccordement({ synthese, erreur, agregateur, label, surOnglet }) {
  if (erreur) return <ErreurBloc erreur={erreur} testid="bridge-synthese-erreur" />;
  if (!synthese) {
    return <Skeleton className="h-40 w-full rounded-lg" data-testid="bridge-synthese-chargement" />;
  }
  const etapes = etapesRaccordement(synthese, agregateur);
  const faites = etapes.filter((e) => e.ok).length;
  const c = synthese.compteurs;
  const ecart = c.actifs_declares - c.actifs_joignables;

  return (
    <section className="rounded-lg border border-neutral-200 bg-white"
             aria-labelledby="verdict-titre" data-testid="bridge-synthese">
      <header className="border-b border-neutral-100 px-4 py-3">
        <h2 id="verdict-titre" className="text-[15px] font-semibold text-neutral-900">
          {label} peut-il piloter ?{' '}
          <span className={faites === 3 ? 'text-emerald-700' : 'text-amber-700'}>
            {faites === 3 ? 'Oui.' : `Pas encore : ${3 - faites} étape${3 - faites > 1 ? 's' : ''} sur 3.`}
          </span>
        </h2>
        <p className="mt-0.5 max-w-[80ch] text-[13px] text-neutral-600" data-testid="bridge-verdict">
          {synthese.verdict}
        </p>
      </header>

      <div className="flex flex-col divide-y divide-neutral-100 md:flex-row md:divide-x md:divide-y-0">
        {etapes.map((etape, i) => (
          <Etape key={etape.cle} etape={etape} rang={i + 1} label={label} surOnglet={surOnglet} />
        ))}
      </div>

      <dl className="grid grid-cols-2 gap-px border-t border-neutral-100 bg-neutral-100 md:grid-cols-4">
        {COMPTEURS.map(([cle, libelle]) => (
          <div key={cle} className="bg-white px-4 py-2.5">
            <dt className="text-[12px] text-neutral-500">{libelle}</dt>
            <dd className="text-[18px] font-semibold tabular-nums text-neutral-900"
                data-testid={`bridge-compteur-${cle}`}>{c[cle]}</dd>
          </div>
        ))}
      </dl>

      {/* « Joignables » sous « déclarés » n'est pas un défaut de raccordement : c'est
          du matériel qui ne répond pas. Le dire évite de chercher la panne dans les clés. */}
      {ecart > 0 && (
        <p className="border-t border-neutral-100 px-4 py-2 text-[12.5px] text-amber-800"
           data-testid="bridge-ecart-joignables">
          {ecart} actif{ecart > 1 ? 's' : ''} déclaré{ecart > 1 ? 's' : ''} ne répond
          {ecart > 1 ? 'ent' : ''} pas en ce moment. Un ordre les concernant partirait sans
          arriver : c&apos;est du matériel, pas du raccordement.
        </p>
      )}
    </section>
  );
}
